"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin, Navigation, Building, Coffee, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const address = "Tacettin Veli, Lalezade Cd. Kartal İş Merkezi Bina No:45 Kat:2 No:6, 38050 Melikgazi/Kayseri";
const businessName = "Diş Hekimi Ayşegül İnneci Uzun";
const mapEmbedUrl = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3113.195222534767!2d35.49215277722817!3d38.713326657542154!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x152b138231641887%3A0x9d265367afddc998!2zRGnFnyBIZWtpbWkgQXnFn2Vnw7xsIMSwTk5FQ8SwIFVaVU4txLBtcGxhbnRvbG9qaSB2ZSBFc3RldGlrIERpxZ8gS2xpbmnEn2kgLyBLQVlTRVLEsA!5e0!3m2!1str!2str!4v1775308147801!5m2!1str!2str";
const directionsUrl = "https://maps.app.goo.gl/JPveWPAhpVCH56YWA";
const landmarks = [{
  id: 1,
  name: "Erciyes Üniversitesi",
  distance: "5 dk sürüş",
  icon: Building
}, {
  id: 2,
  name: "Cumhuriyet Meydanı",
  distance: "10 dk yürüyüş",
  icon: MapPin
}, {
  id: 3,
  name: "Forum Kayseri",
  distance: "12 dk yürüyüş",
  icon: Navigation
}];
export default function GetDirections() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: mapRef,
    isVisible: mapVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="137474" id="konumumuz" className="bg-background text-foreground relative overflow-hidden py-16 md:py-24">
      {}
      <div className="bg-primary/5 absolute top-0 left-0 -translate-x-1/2 -translate-y-1/2 size-96 rounded-full blur-3xl" />
      <div className="bg-secondary/20 absolute right-0 bottom-0 translate-x-1/3 translate-y-1/3 size-[500px] rounded-full blur-3xl" />

      <div className="relative mx-auto max-max-w-7xl px-4 sm:px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`mb-12 text-center transition-all duration-700 ease-out ${headerVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <h2 className="font-serif text-primary mb-4 text-3xl font-light tracking-wider sm:text-4xl md:text-5xl">
            Kliniğimize <span className="text-foreground">Ulaşın</span>
          </h2>
          <p className="text-muted-foreground mx-auto max-w-2xl text-lg font-sans">
            Kayseri'nin kalbinde, modern ve konforlu kliniğimizde sizi ağırlamaktan 
            mutluluk duyarız. Gülüşünüz bizim için değerlidir.
          </p>
        </div>

        {}
        <div ref={mapRef} className={`border-border shadow-sm mb-12 overflow-hidden rounded-sm border transition-all duration-1000 ease-out ${mapVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
          <div className="relative h-[400px] w-full md:h-[500px] lg:h-[600px]">
            <iframe src={mapEmbedUrl} width="100%" height="100%" style={{
            border: 0
          }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title={`Map showing location of ${businessName}`} className="absolute inset-0 size-full grayscale-[90%] contrast-110 opacity-90 filter" />
            {}
            <div className="bg-primary/5 pointer-events-none absolute inset-0 mix-blend-overlay" />
          </div>
        </div>

        {}
        <div ref={contentRef} className={`grid grid-cols-1 gap-8 lg:grid-cols-3 transition-all duration-700 delay-300 ease-out ${contentVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          {}
          <div className="lg:col-span-2">
            <Card className="border-border/50 shadow-sm h-full rounded-sm">
              <CardHeader className="pb-4">
                <div className="bg-secondary text-primary mb-4 inline-flex size-12 items-center justify-center rounded-full">
                  <MapPin className="size-6" />
                </div>
                <CardTitle className="font-serif text-2xl font-normal tracking-wide">
                  Adres Bilgilerimiz
                </CardTitle>
                <CardDescription className="text-base font-sans">
                  Size en iyi hizmeti sunmak için Kayseri'nin en ulaşılabilir noktasındayız.
                </CardDescription>
              </CardHeader>
              <CardContent className="pb-8">
                <div className="bg-muted/50 rounded-sm p-6">
                  <p className="text-foreground text-lg font-medium leading-relaxed font-sans">
                    {address}
                  </p>
                </div>
              </CardContent>
              <CardFooter>
                <Link to={directionsUrl} newTab className="w-full sm:w-auto">
                  <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground w-full gap-2 sm:w-auto rounded-sm tracking-wide">
                    <Navigation className="size-5" />
                    Yol Tarifi Al
                  </Button>
                </Link>
              </CardFooter>
            </Card>
          </div>

          {}
          <div className="lg:col-span-1">
            <Card className="bg-card/50 border-border/50 shadow-sm h-full backdrop-blur-sm rounded-sm">
              <CardHeader>
                <CardTitle className="font-serif flex items-center gap-2 text-xl font-normal tracking-wide">
                  <Coffee className="text-primary size-5" />
                  Yakınındaki Yerler
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-6">
                  {landmarks.map((landmark, i) => {
                  const Icon = landmark.icon;
                  return <li key={landmark.id} data-index={i} className="group flex items-start gap-4">
                        <div className="bg-background border-border text-muted-foreground group-hover:text-primary group-hover:border-primary/50 flex size-10 shrink-0 items-center justify-center rounded-full border transition-colors">
                          <Icon className="size-4" />
                        </div>
                        <div>
                          <h4 className="text-foreground font-medium font-sans">
                            {landmark.name}
                          </h4>
                          <div className="text-muted-foreground mt-1 flex items-center gap-1.5 text-sm font-sans">
                            <Clock className="size-3.5" />
                            <span>{landmark.distance}</span>
                          </div>
                        </div>
                      </li>;
                })}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}